/**
 * Lets a finger drag anything `draggable` — library objects, materials,
 * combinations, lights — and drop it wherever a mouse could.
 *
 * Browsers do not start HTML5 drag-and-drop from touch (Firefox never does), so
 * every library card was mouse-only. Rather than teach each source and each
 * drop target about touch, this translates a touch gesture into the very drag
 * events they already handle: dragstart on the card, dragenter / dragover /
 * dragleave on whatever is under the finger, drop, dragend. The DataTransfer is
 * a constructed one, so the cards' own onDragStart fills it and the scene's own
 * onDrop reads it, unchanged.
 *
 * The gesture is a long press: hold the finger still on a card for
 * LONG_PRESS_MS, then drag. Moving before that is a swipe and scrolls the list;
 * a short touch is a tap and clicks.
 *
 * Why the bridge owns the whole touch on a card. Firefox runs its own long-tap
 * gesture: at ~500 ms it fires `contextmenu`, and once a page cancels that menu
 * it ends the touch with `touchcancel`, after which the finger reports nothing
 * more — the dragged copy froze where it started (Firefox / Linux,
 * 2026-10-01). There is no page-side switch for that gesture; what stops it is
 * cancelling the touchstart, which takes the touch away from the browser's
 * gestures altogether. That also takes away the browser's scrolling and its
 * click for a touch that starts on a card, so both are done here instead:
 * scrolling the card's scroll container (with a short glide on release), and
 * dispatching the click a tap would have produced.
 *
 * If a browser still cancels a drag, the card is not thrown away: it stays
 * picked up, and the next tap drops it there (or, off a drop target, puts it
 * back). Escape puts it back too.
 */

const LONG_PRESS_MS = 400;
/** Movement allowed during the press before it counts as a scroll instead. */
const PRESS_TOLERANCE_PX = 10;
const GHOST_SCALE = 0.6;
/** How long after the placing tap its own pointerup / touchstart / click are swallowed. */
const SWALLOW_MS = 700;
/** Glide after a swipe: velocity kept per 16 ms frame, and where it stops (px/ms). */
const GLIDE_FRICTION = 0.95;
const GLIDE_MIN_SPEED = 0.02;

type Point = { x: number; y: number };

type DragState =
	| { phase: "idle" }
	| {
			phase: "pending";
			source: HTMLElement;
			/** The element actually touched — a tap clicks this, not the card. */
			touched: Element;
			start: Point;
			timer: ReturnType<typeof setTimeout>;
	  }
	| {
			phase: "scrolling";
			scroller: Element;
			last: Point;
			lastTime: number;
			velocity: Point;
	  }
	| {
			phase: "dragging";
			source: HTMLElement;
			dataTransfer: DataTransfer;
			ghost: HTMLElement;
			ghostOffset: Point;
			over: Element | null;
			/** Whether the current target cancelled dragover, i.e. accepts a drop. */
			overAccepts: boolean;
			at: Point;
	  }
	| {
			/** Picked up, finger gone: waiting for the tap that places it. */
			phase: "carrying";
			source: HTMLElement;
			dataTransfer: DataTransfer;
			ghost: HTMLElement;
			at: Point;
	  };

function fire(
	type: string,
	target: Element,
	dataTransfer: DataTransfer,
	{ x, y }: Point,
): boolean {
	const event = new DragEvent(type, {
		bubbles: true,
		cancelable: true,
		composed: true,
		clientX: x,
		clientY: y,
		dataTransfer,
	});
	// dispatchEvent returns false when a handler called preventDefault().
	return !target.dispatchEvent(event);
}

function makeGhost(source: HTMLElement): { ghost: HTMLElement; offset: Point } {
	const rect = source.getBoundingClientRect();
	const ghost = source.cloneNode(true) as HTMLElement;
	Object.assign(ghost.style, {
		position: "fixed",
		left: "0px",
		top: "0px",
		width: `${rect.width}px`,
		height: `${rect.height}px`,
		margin: "0",
		transformOrigin: "0 0",
		opacity: "0.85",
		pointerEvents: "none",
		zIndex: "2147483647",
		boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
		transition: "none",
	});
	document.body.appendChild(ghost);
	// The finger sits in the middle of the shrunken copy.
	return {
		ghost,
		offset: { x: (rect.width * GHOST_SCALE) / 2, y: (rect.height * GHOST_SCALE) / 2 },
	};
}

function placeGhost(ghost: HTMLElement, at: Point, offset: Point) {
	ghost.style.transform = `translate(${at.x - offset.x}px, ${at.y - offset.y}px) scale(${GHOST_SCALE})`;
}

/** The nearest ancestor that can scroll — what a swipe on this card would move. */
function findScroller(from: Element): Element {
	for (let el: Element | null = from.parentElement; el; el = el.parentElement) {
		const style = getComputedStyle(el);
		const canY =
			/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
		const canX =
			/(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth;
		if (canY || canX) return el;
	}
	return document.scrollingElement ?? document.documentElement;
}

/** The click a tap would have produced, now that the touchstart was cancelled. */
function clickAt(target: Element, { x, y }: Point) {
	if (target instanceof HTMLElement) target.focus({ preventScroll: true });
	target.dispatchEvent(
		new MouseEvent("click", {
			bubbles: true,
			cancelable: true,
			composed: true,
			view: window,
			clientX: x,
			clientY: y,
			detail: 1,
		}),
	);
}

const pointOf = (touch: Touch): Point => ({ x: touch.clientX, y: touch.clientY });

// TEMPORARY diagnostics, added while finding the Firefox touchcancel
// (2026-10-01). Remove once finger drag is confirmed working.
const debug = (...args: unknown[]) => console.log("[touchDrag]", ...args);

export default function installTouchDragBridge(): () => void {
	let state: DragState = { phase: "idle" };
	/** Until this time, the placing tap's follow-up events are swallowed. */
	let swallowUntil = 0;
	let glideFrame = 0;

	const swallowing = () => performance.now() < swallowUntil;

	const stopGlide = () => {
		cancelAnimationFrame(glideFrame);
		glideFrame = 0;
	};

	const glide = (scroller: Element, velocity: Point) => {
		let v = { ...velocity };
		let lastTime = performance.now();
		const step = (now: number) => {
			const dt = now - lastTime;
			lastTime = now;
			scroller.scrollTop -= v.y * dt;
			scroller.scrollLeft -= v.x * dt;
			const keep = GLIDE_FRICTION ** (dt / 16);
			v = { x: v.x * keep, y: v.y * keep };
			glideFrame =
				Math.hypot(v.x, v.y) > GLIDE_MIN_SPEED ? requestAnimationFrame(step) : 0;
		};
		glideFrame = requestAnimationFrame(step);
	};

	const reset = () => {
		if (state.phase === "pending") clearTimeout(state.timer);
		if (state.phase === "dragging" || state.phase === "carrying") state.ghost.remove();
		state = { phase: "idle" };
	};

	const startDrag = () => {
		if (state.phase !== "pending") return;
		const { source, start } = state;
		const dataTransfer = new DataTransfer();

		// The card's own onDragStart fills the DataTransfer. If it refuses the
		// drag (preventDefault), there is nothing to carry.
		if (fire("dragstart", source, dataTransfer, start)) {
			debug("dragstart refused by the source");
			reset();
			return;
		}

		debug("long press done → dragging", dataTransfer.types);
		const { ghost, offset } = makeGhost(source);
		placeGhost(ghost, start, offset);
		state = {
			phase: "dragging",
			source,
			dataTransfer,
			ghost,
			ghostOffset: offset,
			over: null,
			overAccepts: false,
			at: start,
		};
		updateTarget(start);
	};

	const updateTarget = (at: Point) => {
		if (state.phase !== "dragging") return;
		// The ghost has pointer-events: none, so this finds what is beneath it.
		const under = document.elementFromPoint(at.x, at.y);
		if (under !== state.over) {
			if (state.over) fire("dragleave", state.over, state.dataTransfer, at);
			if (under) fire("dragenter", under, state.dataTransfer, at);
			state.over = under;
		}
		state.overAccepts = under ? fire("dragover", under, state.dataTransfer, at) : false;
	};

	/** End a drag: drop on `target` if it accepts, then dragend on the source. */
	const finish = (
		source: HTMLElement,
		dataTransfer: DataTransfer,
		target: Element | null,
		accepts: boolean,
		at: Point,
	) => {
		// As with a mouse: drop only on a target whose dragover accepted it.
		if (target && accepts) {
			fire("drop", target, dataTransfer, at);
		} else if (target) {
			fire("dragleave", target, dataTransfer, at);
		}
		fire("dragend", source, dataTransfer, at);
	};

	const onTouchStart = (e: TouchEvent) => {
		// The tap that just placed (or put back) a carried card.
		if (swallowing()) return;
		if (state.phase === "pending" || state.phase === "dragging") {
			// A second finger while pressing or dragging: give up.
			if (e.touches.length > 1) cancel();
			return;
		}
		if (state.phase === "scrolling") {
			reset();
		}
		if (state.phase !== "idle" || e.touches.length !== 1) return;
		const touched = e.target as Element | null;
		const source = touched?.closest?.('[draggable="true"]') as HTMLElement | null;
		if (!touched || !source) return;

		// From here on this touch is ours — see the header for why.
		e.preventDefault();
		stopGlide();
		const start = pointOf(e.touches[0]);
		debug("touchstart → pending", source);
		state = {
			phase: "pending",
			source,
			touched,
			start,
			timer: setTimeout(startDrag, LONG_PRESS_MS),
		};
	};

	const onTouchMove = (e: TouchEvent) => {
		if (state.phase === "idle" || state.phase === "carrying") return;
		const touch = e.touches[0];
		if (!touch) return;
		const at = pointOf(touch);
		e.preventDefault();

		if (state.phase === "pending") {
			const dx = at.x - state.start.x;
			const dy = at.y - state.start.y;
			// Moved before the press completed: a swipe, so scroll.
			if (dx * dx + dy * dy > PRESS_TOLERANCE_PX * PRESS_TOLERANCE_PX) {
				const scroller = findScroller(state.source);
				debug("moved during press → scroll", scroller);
				clearTimeout(state.timer);
				state = {
					phase: "scrolling",
					scroller,
					last: state.start,
					lastTime: performance.now(),
					velocity: { x: 0, y: 0 },
				};
			} else {
				return;
			}
		}

		if (state.phase === "scrolling") {
			const now = performance.now();
			const dx = at.x - state.last.x;
			const dy = at.y - state.last.y;
			state.scroller.scrollTop -= dy;
			state.scroller.scrollLeft -= dx;
			const dt = Math.max(now - state.lastTime, 1);
			// Smoothed, so one jittery sample does not decide the glide.
			state.velocity = {
				x: 0.8 * (dx / dt) + 0.2 * state.velocity.x,
				y: 0.8 * (dy / dt) + 0.2 * state.velocity.y,
			};
			state.last = at;
			state.lastTime = now;
			return;
		}

		// Dragging.
		state.at = at;
		placeGhost(state.ghost, at, state.ghostOffset);
		updateTarget(at);
	};

	const onTouchEnd = (e: TouchEvent) => {
		if (state.phase === "pending") {
			// A tap: the click the cancelled touchstart no longer produces.
			debug("touchend while pending → tap");
			e.preventDefault();
			const { touched, start } = state;
			reset();
			clickAt(touched, start);
			return;
		}
		if (state.phase === "scrolling") {
			const { scroller, velocity, lastTime } = state;
			reset();
			// A finger that stopped before lifting does not fling.
			if (performance.now() - lastTime < 100) glide(scroller, velocity);
			return;
		}
		if (state.phase !== "dragging") return;

		e.preventDefault();
		debug("touchend while dragging", { over: state.over, accepts: state.overAccepts });
		const { source, dataTransfer, over, overAccepts, at } = state;
		state.ghost.remove();
		state = { phase: "idle" };
		finish(source, dataTransfer, over, overAccepts, at);
	};

	// The browser took the touch away mid-drag: keep the card picked up and
	// wait for the tap that places it.
	const onTouchCancel = () => {
		debug("touchcancel in phase", state.phase);
		if (state.phase !== "dragging") {
			reset();
			return;
		}
		const { source, dataTransfer, ghost, over, at } = state;
		if (over) fire("dragleave", over, dataTransfer, at);
		state = { phase: "carrying", source, dataTransfer, ghost, at };
		debug("→ carrying, waiting for a tap");
	};

	// While carrying, the next press anywhere places the card (or puts it back)
	// and does nothing else: the scene must not also select or look around,
	// the library must not also open a card. Window, capture phase, installed
	// at app start — so this runs before the scene's own window listeners.
	const onPointerDown = (e: PointerEvent) => {
		if (state.phase !== "carrying") return;
		e.preventDefault();
		e.stopImmediatePropagation();
		swallowUntil = performance.now() + SWALLOW_MS;

		const { source, dataTransfer, ghost } = state;
		const at = { x: e.clientX, y: e.clientY };
		ghost.remove();
		state = { phase: "idle" };

		const target = document.elementFromPoint(at.x, at.y);
		let accepts = false;
		if (target) {
			fire("dragenter", target, dataTransfer, at);
			accepts = fire("dragover", target, dataTransfer, at);
		}
		debug("placing tap", { target, accepts });
		finish(source, dataTransfer, target, accepts, at);
	};

	const swallow = (e: Event) => {
		if (!swallowing()) return;
		e.preventDefault();
		e.stopImmediatePropagation();
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Escape" && state.phase === "carrying") cancel();
	};

	/** Abandon whatever is in progress; a picked-up card goes back. */
	const cancel = () => {
		debug("cancel in phase", state.phase);
		if (state.phase === "dragging" || state.phase === "carrying") {
			const { source, dataTransfer, at } = state;
			const over = state.phase === "dragging" ? state.over : null;
			reset();
			finish(source, dataTransfer, over, false, at);
			return;
		}
		reset();
	};

	// Belt and braces: no context menu over a card being held.
	const onContextMenu = (e: Event) => {
		if (state.phase !== "idle") {
			debug("contextmenu in phase", state.phase);
			e.preventDefault();
		}
	};

	const capture = { capture: true };
	const active: AddEventListenerOptions = { capture: true, passive: false };
	document.addEventListener("touchstart", onTouchStart, active);
	document.addEventListener("touchmove", onTouchMove, active);
	document.addEventListener("touchend", onTouchEnd, active);
	document.addEventListener("touchcancel", onTouchCancel, capture);
	document.addEventListener("contextmenu", onContextMenu, capture);
	window.addEventListener("pointerdown", onPointerDown, capture);
	window.addEventListener("pointerup", swallow, capture);
	window.addEventListener("click", swallow, capture);
	window.addEventListener("keydown", onKeyDown);

	return () => {
		cancel();
		stopGlide();
		document.removeEventListener("touchstart", onTouchStart, capture);
		document.removeEventListener("touchmove", onTouchMove, capture);
		document.removeEventListener("touchend", onTouchEnd, capture);
		document.removeEventListener("touchcancel", onTouchCancel, capture);
		document.removeEventListener("contextmenu", onContextMenu, capture);
		window.removeEventListener("pointerdown", onPointerDown, capture);
		window.removeEventListener("pointerup", swallow, capture);
		window.removeEventListener("click", swallow, capture);
		window.removeEventListener("keydown", onKeyDown);
	};
}
